import { SMALL_BAG_AMOUNT } from 'utils/boardingOptionsMobile';
import { PrimeDiscountDisplayType, CabinBagsEnum } from 'enums';

const getAverage = (items) =>
  items.reduce((acc, cur) => acc + cur, 0) / items.length;

const getSum = (items) => items.reduce((acc, cur) => acc + cur, 0);

const getCurrency = (cabinBags) =>
  cabinBags[0].priceBreakdown.totalPrice.currency;

export const getMarkups = (cabinBags) => {
  const markups = cabinBags.map((cabinBag) => {
    const prices = cabinBag.prices;

    if (prices.length === 0) {
      return {
        fee: getFee(cabinBags),
        primeFee: getPrimeFee(cabinBags),
      };
    }

    const nonPrimeItems = prices.find((price) => price.type === 'NON_PRIME')
      ?.price?.breakdown?.items;
    const primeItems = prices.find((price) => price.type === 'PRIME')?.price
      ?.breakdown?.items;

    const fee = nonPrimeItems?.find(
      (item) => item.concept === 'MARKUP' || item.concept === 'FEE'
    );
    const prime_fee = primeItems?.find(
      (item) => item.concept === 'PRIME_MARKUP' || item.concept === 'FEE'
    );

    return {
      fee: {
        amount: fee?.price.amount.amount,
        currency: fee?.price.amount.currency,
      },
      primeFee: {
        amount: prime_fee?.price.amount.amount,
        currency: prime_fee?.price.amount.currency,
      },
    };
  });

  return {
    fee: {
      amount: getAverage(markups.map(({ fee }) => fee.amount)),
      currency: markups[0].fee.currency,
    },
    primeFee: {
      amount: getAverage(markups.map(({ primeFee }) => primeFee.amount)),
      currency: markups[0].fee.currency,
    },
  };
};

export const getPrices = (cabinBags) => ({
  [CabinBagsEnum.CABIN_BAG]: {
    amount: getAverage(
      cabinBags.map(({ priceBreakdown: { totalPrice } }) => totalPrice.amount)
    ),
    currency: getCurrency(cabinBags),
  },

  [CabinBagsEnum.SMALL_BAG]: {
    amount: SMALL_BAG_AMOUNT,
    currency: getCurrency(cabinBags),
  },
});

export const hasPrimePrices = (cabinBags) =>
  cabinBags.some(
    (item) =>
      item.priceBreakdown.totalPrimePrice?.amount !==
      item.priceBreakdown.totalPrice?.amount
  );

export const getPrimePrices = (cabinBags) => ({
  [CabinBagsEnum.CABIN_BAG]: {
    amount: getAverage(
      cabinBags.map(
        ({ priceBreakdown: { totalPrimePrice } }) => totalPrimePrice.amount
      )
    ),
    currency: getCurrency(cabinBags),
  },
  [CabinBagsEnum.SMALL_BAG]: {
    amount: SMALL_BAG_AMOUNT,
    currency: getCurrency(cabinBags),
  },
});

export const getFee = (cabinBags) => ({
  amount: getAverage(
    cabinBags.map(({ priceBreakdown: { fee } }) => fee.amount)
  ),
  currency: cabinBags[0].priceBreakdown.fee.currency,
});

export const getPrimeFee = (cabinBags) => ({
  amount: getAverage(
    cabinBags.map(({ priceBreakdown: { primeFee } }) => primeFee.amount)
  ),
  currency: cabinBags[0].priceBreakdown.primeFee.currency,
});

export const getCarrierProperties = (cabinBags) =>
  cabinBags[0]?.carrierConfiguration ?? {
    penaltyFee: { amount: 0, currency: 'EUR' },
  };

export const getPrimeDiscountInfo = (cabinBags) => {
  if (!hasPrimePrices(cabinBags)) {
    return {
      discountDisplayType: PrimeDiscountDisplayType.PERCENTAGE,
      percentage: 0,
      amount: 0,
      currency: getCurrency(cabinBags),
    };
  }

  const prices = cabinBags.map(
    ({ priceBreakdown: { totalPrice } }) => totalPrice.amount
  );
  const primePrices = cabinBags.map(
    ({ priceBreakdown: { totalPrimePrice } }) => totalPrimePrice.amount
  );
  const totalPrice = getSum(prices);
  const totalPrimePrice = getSum(primePrices);
  const discountAmount = totalPrice - totalPrimePrice;
  const discountPercentage = (discountAmount * 100) / totalPrice;

  // const discountDisplayType =
  //   discountPercentage < PRIME_PRICE_PERCENTAGE_THRESHOLD
  //     ? PrimeDiscountDisplayType.AMOUNT
  //     : PrimeDiscountDisplayType.PERCENTAGE;

  return {
    discountDisplayType: PrimeDiscountDisplayType.PERCENTAGE, // TODO use proper value when total amount issue is fixed
    percentage: Math.ceil(discountPercentage),
    amount: discountAmount,
    currency: getCurrency(cabinBags),
  };
};

export const getDebugInfo = (cabinBags) =>
  cabinBags.map((cabinBag) => cabinBag.debugInfo).join('');

export const getCarrierId = (cabinBags) => cabinBags?.[0]?.carrier ?? '';
